import React, { useState } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { saveClassificationResult } from "../utils/saveClassificationResult";

function BatchPredictionSummaryDialog({
  open,
  onClose,
  categoryCounts,
  lowConfidenceCount,
  userId,
  selectedFolder,
  files,
  classificationResults,
  onSaveComplete,
}) {
  // Filter files and categoryCounts to only include files with confidence >= 0.7
  const confidentFiles = files.filter((file) => file.confidence >= 0.7);

  // Recalculate categoryCounts based on confidentFiles
  const confidentCategoryCounts = {};
  confidentFiles.forEach((file) => {
    const category =
      classificationResults[files.indexOf(file)]?.predicted_category || "";
    if (category) {
      confidentCategoryCounts[category] =
        (confidentCategoryCounts[category] || 0) + 1;
    }
  });

  // Prepare data for bar chart using confidentCategoryCounts
  const data = Object.entries(confidentCategoryCounts).map(
    ([category, count]) => ({
      category,
      count,
    })
  );

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveProgress, setSaveProgress] = useState(0);

  const handleClose = async () => {
    if (isSaving) return; // Prevent closing while saving

    setIsSaving(true);
    setSaveMessage("Saving Files...");
    setSaveProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const classificationResult = classificationResults[i];
        const confirmedCategory =
          classificationResult.confirmedCategory ||
          classificationResult.predicted_category ||
          "";

        // Only save files with confidence >= 0.7
        if (file.confidence >= 0.7) {
          await saveClassificationResult(
            userId,
            selectedFolder,
            file,
            classificationResult,
            confirmedCategory,
            classificationResult.summary || ""
          );

          // Mark file as predicted in metadata
          try {
            const { updateIsPredictedMetadata } = await import(
              "../../../../services/StorageServices"
            );
            await updateIsPredictedMetadata(file.storagePath, true);
          } catch (err) {
            console.error(
              "Failed to update predicted metadata for file:",
              file.id,
              err
            );
          }
        }

        if (file.confidence >= 0.7) {
          setSaveProgress((prev) => prev + 1);
        }
      }

      setSaveMessage("Saving done");
      setTimeout(() => {
        setIsSaving(false);
        setSaveMessage("");
        setSaveProgress(0);
        onSaveComplete && onSaveComplete();
        onClose();
      }, 1500);
    } catch (error) {
      setSaveMessage("Error saving files: " + error.message);
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Batch Prediction Summary</DialogTitle>
      <DialogContent>
        {isSaving ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: 200,
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {saveMessage}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {saveProgress} /{" "}
              {files.filter((file) => file.confidence >= 0.7).length} files
              saved
            </Typography>
          </Box>
        ) : (
          <>
            {lowConfidenceCount > 0 ? (
              <Alert severity="warning" icon={<WarningAmberIcon />}>
                <Typography variant="subtitle1">
                  Total files with confidence below 70%: {lowConfidenceCount}
                </Typography>
              </Alert>
            ) : (
              <Typography
                variant="subtitle1"
                gutterBottom
                color="text.secondary"
              >
                All files are above 70% confidence.
              </Typography>
            )}

            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="category"
                    tickFormatter={(value) => value.replace(/_/g, "\n")}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="contained"
          color="primary"
          disabled={isSaving}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BatchPredictionSummaryDialog;
